import { WorkflowStatus } from 'src/constants/commonEnums';

// Entity types that should show the new workflows with/without runs view
export const ENTITY_TYPES_WITH_SPLIT_VIEW = ['anchor_point'];

export interface EntityData {
	WorkflowType: string;
	workflowDefinitionName: string;
	EntityName: string;
	CycleId: string;
	Status: string;
	LastStatusUpdateTime?: string;
	ValidationExecutionId?: string;
	EntityData?: {
		Approver?: string;
		validationExecutionId?: string;
		[key: string]: unknown;
	};
	EntityAttributes?: {
		ApprovalId?: string;
		startedByParent?: string;
	};
	[key: string]: unknown;
}

export const isStartedByParent = (entityAttributes?: EntityData['EntityAttributes']): boolean =>
	Boolean(entityAttributes?.startedByParent);

export interface OrderedWorkflowEntity<T> {
	entity: T;
	depth: number;
}

export const orderWorkflowsByParent = <
	T extends {
		WorkflowType: string;
		EntityAttributes?: EntityData['EntityAttributes'];
	}
>(
		entities: T[]
	): OrderedWorkflowEntity<T>[] => {
	const childrenByParent = new Map<string, T[]>();
	const parents = new Set(entities.map((e) => e.WorkflowType));

	entities.forEach((entity) => {
		const parent = entity.EntityAttributes?.startedByParent;

		if (parent && parents.has(parent)) {
			const siblings = childrenByParent.get(parent) ?? [];

			siblings.push(entity);
			childrenByParent.set(parent, siblings);
		}
	});

	const ordered: OrderedWorkflowEntity<T>[] = [];

	entities.forEach((entity) => {
		const parent = entity.EntityAttributes?.startedByParent;

		if (parent && parents.has(parent)) return;

		ordered.push({ entity, depth: 0 });
		(childrenByParent.get(entity.WorkflowType) ?? []).forEach((child) =>
			ordered.push({ entity: child, depth: 1 })
		);
	});

	return ordered;
};

export interface WorkflowDefinition {
	workflowId: string;
	workflowDefinitionName: string;
	[key: string]: unknown;
}

export const workflowWithRuns = (workflow: EntityData): boolean => {
	return workflow.Status !== undefined && workflow.Status !== null;
};

const STATUS_LEADING: WorkflowStatus[] = [WorkflowStatus.RUNNING];

const STATUS_TRAILING: WorkflowStatus[] = [
	WorkflowStatus.COMPLETED,
	WorkflowStatus.DRAFT,
];

const ORDERED_STATUSES: WorkflowStatus[] = [
	...STATUS_LEADING,
	...Object.values(WorkflowStatus).filter(
		(status) =>
			!STATUS_LEADING.includes(status) && !STATUS_TRAILING.includes(status)
	),
	...STATUS_TRAILING,
];

const STATUS_TITLE_OVERRIDES: Partial<Record<WorkflowStatus, string>> = {
	[WorkflowStatus.RUNNING]: 'In progress',
};

const toTitle = (status: WorkflowStatus): string =>
	STATUS_TITLE_OVERRIDES[status] ??
	status.charAt(0).toUpperCase() +
		status.slice(1).toLowerCase().replace(/_/g, ' ');

const DEFAULT_STATUS_ORDER = ORDERED_STATUSES.length;

export const getStatusOrder = (status?: string): number => {
	const index = ORDERED_STATUSES.indexOf(status as WorkflowStatus);

	return index === -1 ? DEFAULT_STATUS_ORDER : index;
};

export const sortWorkflowsByStatus = <T extends { Status?: string }>(
	workflows: T[]
): T[] => {
	return [...workflows].sort(
		(a, b) => getStatusOrder(a.Status) - getStatusOrder(b.Status)
	);
};

const getRecencyValue = (value?: string): number => {
	if (!value) {
		return 0;
	}

	const asNumber = Number(value);

	if (!Number.isNaN(asNumber)) {
		return asNumber;
	}

	const parsed = Date.parse(value);

	return Number.isNaN(parsed) ? 0 : parsed;
};

export const sortWorkflowsByRecency = <
	T extends { LastStatusUpdateTime?: string }
>(
		workflows: T[]
	): T[] => {
	return [...workflows].sort(
		(a, b) =>
			getRecencyValue(b.LastStatusUpdateTime) -
			getRecencyValue(a.LastStatusUpdateTime)
	);
};

export interface WorkflowStatusSection<T> {
	key: string;
	title: string;
	workflows: T[];
}

export const OTHER_STATUS_SECTION_KEY = 'OTHER';
const OTHER_STATUS_SECTION_TITLE = 'Other';

const KNOWN_STATUSES = new Set<string>(ORDERED_STATUSES);

export const groupWorkflowsByStatus = <T extends { Status?: string }>(
	workflows: T[]
): WorkflowStatusSection<T>[] => {
	const workflowsByStatus = new Map<string, T[]>();

	workflows.forEach((workflow) => {
		const statusKey =
			workflow.Status && KNOWN_STATUSES.has(workflow.Status)
				? workflow.Status
				: OTHER_STATUS_SECTION_KEY;
		const groupedWorkflows = workflowsByStatus.get(statusKey);

		if (groupedWorkflows) {
			groupedWorkflows.push(workflow);
		} else {
			workflowsByStatus.set(statusKey, [workflow]);
		}
	});

	const sections: WorkflowStatusSection<T>[] = ORDERED_STATUSES.map((status) => ({
		key: status,
		title: toTitle(status),
		workflows: workflowsByStatus.get(status) ?? [],
	}));
	const otherWorkflows = workflowsByStatus.get(OTHER_STATUS_SECTION_KEY);

	if (otherWorkflows) {
		sections.push({
			key: OTHER_STATUS_SECTION_KEY,
			title: OTHER_STATUS_SECTION_TITLE,
			workflows: otherWorkflows,
		});
	}

	return sections.filter((section) => section.workflows.length > 0);
};

export const workflowWithoutRuns = (
	workflow: WorkflowDefinition,
	entityType: string
): boolean => {
	return ENTITY_TYPES_WITH_SPLIT_VIEW.includes(entityType);
};

export const filterWorkflows = (
	entityData: EntityData[],
	workflowDefinitions: WorkflowDefinition[],
	entityType: string
) => {
	const workflowsWithRuns = sortWorkflowsByStatus(
		entityData.filter(workflowWithRuns)
	);
	const workflowsWithRunsIds = workflowsWithRuns.map((wf) => wf.WorkflowType);

	const workflowsWithoutRuns = workflowDefinitions
		.filter((wf) => workflowWithoutRuns(wf, entityType))
		.filter(
			(wf) =>
				!workflowsWithRunsIds.includes(wf.workflowId) &&
				!workflowsWithRunsIds.includes(wf.workflowDefinitionName)
		);

	return { workflowsWithRuns, workflowsWithoutRuns };
};

export const shouldShowSplitView = (entityType?: string): boolean => {
	return entityType ? ENTITY_TYPES_WITH_SPLIT_VIEW.includes(entityType) : false;
};
